/**
 * System Overview Page
 * Central hub linking to all pages and features across all subdomains
 */

import React from 'react';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { Card } from '@/components/ui/card';

interface SystemLink {
  name: string;
  url: string;
  description: string;
  status: 'live' | 'dev' | 'planned';
  icon: string;
}

export default function SystemOverview() {
  const systems: { category: string; items: SystemLink[] }[] = [
    {
      category: 'Customer-Facing Sites',
      items: [
        { name: 'Main Website', url: 'https://rightawayhome.com', description: 'Public website with booking widget, coverage lookup', status: 'live', icon: '🏠' },
        { name: 'Homeowner Portal', url: 'https://rightawayhome.com/portal', description: 'Self-service portal for warranty management', status: 'live', icon: '👤' },
        { name: 'Parts Request Form', url: 'https://forms.rightawayhome.com', description: 'Technician parts request with AI OCR', status: 'live', icon: '🔧' },
        { name: 'Parts Tracker', url: 'https://forms.rightawayhome.com/tracker.html', description: 'Warehouse dashboard for part requests', status: 'live', icon: '📦' }
      ]
    },
    {
      category: 'Admin & Operations',
      items: [
        { name: 'Admin Dashboard', url: '/', description: 'Main admin overview', status: 'live', icon: '📊' },
        { name: 'Dispatch System', url: 'https://dispatch.rightawayhome.com', description: 'Dispatch board, CRM, scheduling (IN DEV)', status: 'dev', icon: '🚛' },
        { name: 'Call Center', url: '/call-center', description: 'Contact center operations', status: 'live', icon: '📞' },
        { name: 'Forms Library', url: '/forms', description: 'Legal forms management', status: 'live', icon: '📄' }
      ]
    },
    {
      category: 'Warranty & Claims',
      items: [
        { name: 'Warranty Overview', url: '/warranty', description: 'Warranty system dashboard', status: 'live', icon: '🛡️' },
        { name: 'Claims', url: '/warranty/claims', description: 'Claims processing', status: 'live', icon: '📋' },
        { name: 'Agreements', url: '/warranty/agreements', description: 'Agreement management', status: 'live', icon: '📜' },
        { name: 'Equipment', url: '/warranty/equipment', description: 'Equipment registry', status: 'live', icon: '❄️' }
      ]
    },
    {
      category: 'Partner Network',
      items: [
        { name: 'Partner Portal', url: 'https://partner.rightawayhome.com', description: 'Partner dashboard', status: 'live', icon: '🤝' },
        { name: 'Contractor Portal', url: 'https://contractor.rightawayhome.com', description: 'Contractor claims', status: 'live', icon: '🔨' },
        { name: 'Vendors', url: '/vendors', description: 'Vendor management', status: 'live', icon: '🏢' }
      ]
    }
  ];

  return (
    <AdminPageLayout
      title="System Overview"
      subtitle="All pages and features across RahsMatch platform"
    >
      {systems.map((section, idx) => (
        <Card key={idx} className="p-6">
          <h2 className="text-xl font-semibold mb-4">{section.category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.items.map((item, itemIdx) => (
              <a
                key={itemIdx}
                href={item.url}
                target={item.url.startsWith('http') ? '_blank' : undefined}
                rel={item.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="block border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{item.icon}</span>
                    <h3 className="font-semibold text-gray-900">{item.name}</h3>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    item.status === 'live' ? 'bg-green-100 text-green-800' :
                    item.status === 'dev' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.status.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{item.description}</p>
                {item.url.startsWith('http') && (
                  <div className="mt-2 text-xs text-blue-600">
                    {item.url} →
                  </div>
                )}
              </a>
            ))}
          </div>
        </Card>
      ))}
    </AdminPageLayout>
  );
}
